import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, Dumbbell, UtensilsCrossed, Snowflake, MessageSquare, Megaphone, CreditCard, UserPlus, Ticket, Lightbulb, BarChart3, Sparkles, Sliders, Trophy, BookOpen, ShieldCheck, Globe, Tag, X } from 'lucide-react';
import { RegenLogo } from './RegenLogo';
import { useAuth } from '@/lib/auth';
import { useTheme } from '@/lib/theme';
import { LogOut, Moon, Sun, CalendarClock } from 'lucide-react';
import { useEffect } from 'react';

export const ADMIN_NAV_ITEMS = [
  { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard, testid: 'admin-nav-dashboard' },
  { to: '/admin/clients', label: 'Clients', icon: Users, testid: 'admin-nav-clients' },
  { to: '/admin/subscriptions', label: 'Subscriptions', icon: CalendarClock, testid: 'admin-nav-subscriptions' },
  { to: '/admin/training', label: 'Training', icon: Dumbbell, testid: 'admin-nav-training' },
  { to: '/admin/activities', label: 'Activities', icon: Trophy, testid: 'admin-nav-activities' },
  { to: '/admin/nutrition', label: 'Nutrition', icon: UtensilsCrossed, testid: 'admin-nav-nutrition' },
  { to: '/admin/foods', label: 'Food Library', icon: UtensilsCrossed, testid: 'admin-nav-foods' },
  { to: '/admin/recovery', label: 'Recovery', icon: Snowflake, testid: 'admin-nav-recovery' },
  { to: '/admin/ai', label: 'AI Generators', icon: Sparkles, testid: 'admin-nav-ai' },
  { to: '/admin/messages', label: 'Messages', icon: MessageSquare, testid: 'admin-nav-messages' },
  { to: '/admin/broadcasts', label: 'Broadcasts', icon: Megaphone, testid: 'admin-nav-broadcasts' },
  { to: '/admin/payments', label: 'Payments', icon: CreditCard, testid: 'admin-nav-payments' },
  { to: '/admin/pricing', label: 'Country Pricing', icon: Globe, testid: 'admin-nav-pricing' },
  { to: '/admin/website-pricing', label: 'Website Pricing', icon: Tag, testid: 'admin-nav-website-pricing' },
  { to: '/admin/referrals', label: 'Referrals', icon: UserPlus, testid: 'admin-nav-referrals' },
  { to: '/admin/access-codes', label: 'Access Codes', icon: Ticket, testid: 'admin-nav-codes' },
  { to: '/admin/tips', label: 'Tips', icon: Lightbulb, testid: 'admin-nav-tips' },
  { to: '/admin/academy', label: 'Academy', icon: BookOpen, testid: 'admin-nav-academy' },
  { to: '/admin/analytics', label: 'Analytics', icon: BarChart3, testid: 'admin-nav-analytics' },
  { to: '/admin/scoring', label: 'Scoring Weights', icon: Sliders, testid: 'admin-nav-scoring' },
  { to: '/admin/privacy', label: 'Privacy & Compliance', icon: ShieldCheck, testid: 'admin-nav-privacy' },
];

export function AdminSidebar({ open = false, onClose = () => {} }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const loc = useLocation();
  useEffect(() => { onClose(); /* close drawer on route change */ }, [loc.pathname]);

  return (
    <>
      {/* Mobile backdrop */}
      <div onClick={onClose} className={`fixed inset-0 bg-black/70 z-40 lg:hidden transition-opacity ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} />

      <aside className={`fixed inset-y-0 left-0 w-64 bg-[var(--c-elev)] border-r border-[var(--c-hair)] flex flex-col z-50 transition-transform duration-300
        ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="px-6 py-6 border-b border-[var(--c-hair)] flex items-center justify-between">
          <div>
            <RegenLogo size="md" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-1">Admin Console</div>
          </div>
          <button onClick={onClose} className="lg:hidden w-8 h-8 rounded-full hover:bg-[var(--c-film)] flex items-center justify-center" data-testid="sidebar-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5 no-scrollbar">
          {ADMIN_NAV_ITEMS.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all relative ${
                  isActive
                    ? 'bg-gradient-to-r from-[#00C8FF]/12 to-transparent text-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-[var(--c-film)]'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-0.5 rounded-r bg-[#00C8FF] shadow-[0_0_8px_rgba(0,200,255,0.7)]" />}
                  <Icon className="w-4 h-4" strokeWidth={isActive ? 2.5 : 1.8} />
                  {label}
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-[var(--c-hair)] p-3">
          <button
            onClick={toggle}
            data-testid="admin-theme-toggle"
            className="w-full flex items-center justify-between px-3 py-2 mb-1 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-[var(--c-film)]"
          >
            <span className="flex items-center gap-2">
              {theme === 'dark' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              Appearance
            </span>
            <span className="pill bg-[var(--c-film)] text-foreground text-[10px]">{theme === 'dark' ? 'Dark Blue' : 'Light'}</span>
          </button>
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#00C8FF] to-[var(--c-green)] flex items-center justify-center text-[#081726] font-bold text-sm">
              {(user?.name || 'JP').split(' ').map(p => p[0]).slice(0,2).join('')}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{user?.name || 'Coach JP'}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{user?.role}</div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-[var(--c-film)]"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>
    </>
  );
}
